import React, { useState, useEffect } from 'react';
import {
  Table,
  Button,
  Drawer,
  Form,
  Input,
  Select,
  Space,
  message,
  Tag,
  Switch,
  Tooltip,
} from 'antd';
import {
  EditOutlined,
  PlusOutlined,
  KeyOutlined,
  EyeOutlined,
  CloseCircleOutlined,
  ReloadOutlined,
  UserOutlined,
} from '@ant-design/icons';
import rbacService from '../../services/rbacService';
import partnerNetworkService from '../../services/partnerNetworkService';
import AssignRolesDrawer from './AssignRolesDrawer';
import ViewPermissionsModal from './ViewPermissionsModal';
import ReassignRmPartnersModal from './ReassignRmPartnersModal';
import useBreakpoint from '../../hooks/useBreakpoint';
import { useAuth } from '../../contexts/AuthContext';
import { PERMISSIONS } from '../../config/permissions';

const { Option } = Select;

const filterSelectOption = (input, option) =>
  String(option?.label ?? '')
    .toLowerCase()
    .includes(input.trim().toLowerCase());

const SEARCHABLE_SELECT_PROPS = {
  showSearch: true,
  optionFilterProp: 'label',
  filterOption: filterSelectOption,
};

const resolvePrimaryRoleId = (record, rolesList) => {
  const roleNames = Array.isArray(record?.roles) ? record.roles : [];
  if (!roleNames.length || !rolesList.length) return undefined;
  const matches = rolesList.filter((role) => roleNames.includes(role.name));
  if (!matches.length) return undefined;
  matches.sort(
    (a, b) => (a.level ?? a.sub_role_level ?? 999) - (b.level ?? b.sub_role_level ?? 999),
  );
  return matches[0].id;
};

const DEFAULT_FILTERS = {
  search: '',
  is_active: undefined,
  role_id: undefined,
  entity_type_id: undefined,
  manager_id: undefined,
};

const DEFAULT_SORT = { sort_by: 'created_at', sort_order: 'desc' };
const DEFAULT_PAGE_SIZE = 10;

export default function UsersManagement() {
  const { hasPermission, isSuperAdmin } = useAuth();
  const canReassignRmPartners = isSuperAdmin || hasPermission(PERMISSIONS.RBAC_HIERARCHY_EDIT);

  // ── Master data ────────────────────────────────────────────────────
  const [roles, setRoles] = useState([]);
  const [entityTypes, setEntityTypes] = useState([]);
  const [allManagers, setAllManagers] = useState([]);
  const [managers, setManagers] = useState([]);
  const [loadingManagers, setLoadingManagers] = useState(false);

  // ── Table data ─────────────────────────────────────────────────────
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);

  // ── Filter / pagination / sort state ──────────────────────────────
  const [filters, setFilters] = useState({ ...DEFAULT_FILTERS });
  const [searchInput, setSearchInput] = useState('');
  const [pagination, setPagination] = useState({ current: 1, pageSize: DEFAULT_PAGE_SIZE, total: 0 });
  const [sortState, setSortState] = useState({ ...DEFAULT_SORT });

  // ── Drawer / modal state ───────────────────────────────────────────
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [assignRolesVisible, setAssignRolesVisible] = useState(false);
  const [permissionsModalVisible, setPermissionsModalVisible] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [reassignModalOpen, setReassignModalOpen] = useState(false);
  const [pendingDeactivateValues, setPendingDeactivateValues] = useState(null);

  const [form] = Form.useForm();
  const watchEntityType = Form.useWatch('entity_type', form);
  const watchRoleId = Form.useWatch('role_id', form);
  const isAboveSm = useBreakpoint('sm');

  useEffect(() => {
    fetchUsers({ page: 1, pageSize: DEFAULT_PAGE_SIZE, currentFilters: DEFAULT_FILTERS, sort: DEFAULT_SORT });
    fetchRoles();
    fetchEntityTypes();
    fetchAllManagers();
  }, []);

  // ── Fetch users ────────────────────────────────────────────────────
  const fetchUsers = async ({
    page = 1,
    pageSize = pagination.pageSize,
    currentFilters = filters,
    sort = sortState,
  } = {}) => {
    setLoading(true);
    try {
      const params = {
        page,
        limit: pageSize,
        sort_by: sort.sort_by,
        sort_order: sort.sort_order,
        exclude_entity_types: 'PARTNER,INVESTOR',
        ...(currentFilters.search ? { search: currentFilters.search } : {}),
        ...(currentFilters.is_active !== undefined ? { is_active: currentFilters.is_active } : {}),
        ...(currentFilters.role_id != null ? { role_id: currentFilters.role_id } : {}),
        ...(currentFilters.entity_type_id != null ? { entity_type_id: currentFilters.entity_type_id } : {}),
        ...(currentFilters.manager_id != null ? { manager_id: currentFilters.manager_id } : {}),
      };

      const result = await rbacService.getUsers(params);

      if (result.success) {
        let data = result.data;
        let total = 0;

        if (data && typeof data === 'object' && !Array.isArray(data)) {
          // If pagination is deeply nested in data (backend format)
          if (data.pagination) {
            total = data.pagination.total ?? data.pagination.totalCount ?? 0;
          } else {
            total = data.total ?? data.totalCount ?? data.count ?? 0;
          }
          
          if (data.users && Array.isArray(data.users)) {
            data = data.users;
          } else if (data.data && Array.isArray(data.data)) {
            data = data.data;
          } else if (data.items && Array.isArray(data.items)) {
            data = data.items;
          }
        }

        if (result.pagination) {
          total = result.pagination.total ?? result.pagination.totalCount ?? total;
        }

        const usersArray = Array.isArray(data) ? data : [];
        setUsers(usersArray);
        setPagination({ current: page, pageSize, total: total || usersArray.length });
      } else {
        message.error(result.message || 'Failed to fetch users');
        setUsers([]);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      message.error('Error loading users');
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchRoles = async () => {
    try {
      const result = await rbacService.getRoles();
      if (result.success) {
        let rawData = result.data;
        if (rawData && typeof rawData === 'object' && !Array.isArray(rawData)) {
          rawData = rawData.roles || rawData.data || rawData.items || [];
        }
        const rolesArray = Array.isArray(rawData) ? rawData : [];
        setRoles(rolesArray.filter(r => r.name !== 'Partner' && r.name !== 'Investor'));
      }
    } catch { setRoles([]); }
  };

  const fetchEntityTypes = async () => {
    try {
      const result = await rbacService.getEntityTypes();
      if (result.success) {
        const data = Array.isArray(result.data) ? result.data : [];
        setEntityTypes(data);
        if (data.length === 1 && !selectedUser) form.setFieldValue('entity_type', data[0].value);
      }
    } catch { setEntityTypes([]); }
  };

  const filterRolesByEntityType = (rolesList, entityTypeCode) => {
    if (!entityTypeCode) return rolesList;
    return rolesList.filter((role) => {
      if (!role.entity_types?.length) return true;
      return role.entity_types.some((et) => et.code === entityTypeCode);
    });
  };

  const fetchManagers = async (subRoleLevel, excludeUserId = null, ensureManager = null, entityTypeId = null) => {
    if (subRoleLevel == null) { setManagers([]); return; }
    setLoadingManagers(true);
    setManagers([]);
    try {
      const result = await rbacService.getManagers(subRoleLevel, excludeUserId, entityTypeId);
      if (result.success && Array.isArray(result.data)) {
        let list = result.data;
        if (ensureManager?.id && !list.some((m) => m.id === ensureManager.id)) {
          list = [
            { id: ensureManager.id, name: ensureManager.name, role: ensureManager.role ?? '' },
            ...list,
          ];
        }
        setManagers(list);
      } else {
        setManagers([]);
      }
    } catch { setManagers([]); }
    finally { setLoadingManagers(false); }
  };

  const fetchAllManagers = async () => {
    try {
      const result = await rbacService.getAllManagers();
      if (result.success && Array.isArray(result.data)) setAllManagers(result.data);
      else setAllManagers([]);
    } catch { setAllManagers([]); }
  };

  // ── Filter handlers ────────────────────────────────────────────────
  const handleSearch = (value) => {
    const newFilters = { ...filters, search: value };
    setFilters(newFilters);
    fetchUsers({ page: 1, pageSize: pagination.pageSize, currentFilters: newFilters, sort: sortState });
  };

  const handleFilterChange = (key, value) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
    fetchUsers({ page: 1, pageSize: pagination.pageSize, currentFilters: newFilters, sort: sortState });
  };

  const handleClearFilters = () => {
    const cleared = { ...DEFAULT_FILTERS };
    setFilters(cleared);
    setSearchInput('');
    fetchUsers({ page: 1, pageSize: pagination.pageSize, currentFilters: cleared, sort: sortState });
  };

  const handleTableChange = (newPagination, _, sorter) => {
    const newSort = sorter?.field
      ? { sort_by: sorter.field, sort_order: sorter.order === 'ascend' ? 'asc' : 'desc' }
      : sortState;
    setSortState(newSort);
    fetchUsers({
      page: newPagination.current,
      pageSize: newPagination.pageSize,
      currentFilters: filters,
      sort: newSort,
    });
  };

  const activeFilterCount = [
    !!filters.search,
    filters.is_active !== undefined,
    filters.role_id != null,
    filters.entity_type_id != null,
    filters.manager_id != null,
  ].filter(Boolean).length;

  // ── CRUD handlers ──────────────────────────────────────────────────
  const handleCreate = () => {
    setSelectedUser(null);
    setManagers([]);
    form.resetFields();
    setDrawerVisible(true);
  };

  const getEditUserRoleLevel = () => {
    const roleId = form.getFieldValue('role_id');
    if (roleId != null) {
      const role = roles.find((r) => r.id === roleId);
      if (role) return role.level ?? role.sub_role_level ?? 10;
    }
    return selectedUser?.level ?? selectedUser?.role_level ?? selectedUser?.primary_role_level ?? 10;
  };

  const handleFormValuesChange = (changedValues, allValues) => {
    const { entity_type, role_id } = allValues;
    const entityTypeRow = entityTypes.find((et) => et.value === entity_type);
    const entityTypeId = entityTypeRow?.id ?? entityTypeRow?.uuid;

    if (selectedUser) {
      const roleChanged = Object.prototype.hasOwnProperty.call(changedValues, 'role_id');
      if (!roleChanged) return;
      form.setFieldValue('manager_id', undefined);
      if (role_id == null) { setManagers([]); return; }
      const role = roles.find((r) => r.id === role_id);
      const subRoleLevel = role?.level ?? role?.sub_role_level ?? getEditUserRoleLevel();
      fetchManagers(subRoleLevel, selectedUser.id, null, entityTypeId);
      return;
    }

    const changed =
      Object.prototype.hasOwnProperty.call(changedValues, 'entity_type') ||
      Object.prototype.hasOwnProperty.call(changedValues, 'role_id');
    if (!changed) return;
    form.setFieldValue('manager_id', undefined);
    if (!entity_type || role_id == null) { setManagers([]); return; }
    const role = roles.find((r) => r.id === role_id);
    fetchManagers(role?.level ?? role?.sub_role_level ?? 10, null, null, entityTypeId);
  };

  const handleEdit = async (record) => {
    setSelectedUser(record);
    const entityTypeCode = record.entity_type;
    const managerId = record.manager_id ?? undefined;
    const ensureManager = managerId
      ? { id: managerId, name: record.manager_name, role: record.manager_role }
      : null;

    let roleId = resolvePrimaryRoleId(record, roles);
    try {
      const rolesResult = await rbacService.getUserRoles(record.id);
      if (rolesResult.success && Array.isArray(rolesResult.data) && rolesResult.data.length > 0) {
        roleId = rolesResult.data[0].id;
      }
    } catch {
      /* use list row roles as fallback */
    }

    form.setFieldsValue({
      name: record.name,
      email: record.email,
      mobile: record.mobile,
      entity_type: entityTypeCode,
      role_id: roleId,
      is_active: record.is_active !== false,
      manager_id: managerId,
    });
    setDrawerVisible(true);

    const role = roles.find((r) => r.id === roleId);
    const subRoleLevel = role?.level ?? role?.sub_role_level
      ?? record.level ?? record.role_level ?? record.primary_role_level ?? 10;
    const entityTypeRow = entityTypes.find((et) => et.value === entityTypeCode || et.label === entityTypeCode);

    if (roleId != null) {
      fetchManagers(subRoleLevel, record.id, ensureManager, entityTypeRow?.id ?? entityTypeRow?.uuid).then(() => {
        if (managerId) form.setFieldValue('manager_id', managerId);
      });
    } else {
      setManagers([]);
    }
  };

  const submitUserUpdate = async (values) => {
    const payload = {
      name: values.name,
      email: values.email,
      mobile: values.mobile,
      is_active: values.is_active,
    };
    if (values.manager_id != null) payload.manager_id = values.manager_id;
    if (values.role_id != null) payload.role_id = values.role_id;
    const result = await rbacService.updateUser(selectedUser.id, payload);
    if (result.success) {
      message.success('User updated successfully');
      setDrawerVisible(false);
      fetchUsers({
        page: pagination.current,
        pageSize: pagination.pageSize,
        currentFilters: filters,
        sort: sortState,
      });
    } else {
      message.error(result.message || 'Failed to update user');
    }
  };

  const handleSubmit = async (values) => {
    try {
      if (selectedUser) {
        const isDeactivating =
          values.is_active === false && selectedUser.is_active !== false;

        if (isDeactivating && canReassignRmPartners) {
          try {
            const partners = await partnerNetworkService.getPartnersByRm(selectedUser.id);
            if (partners.length > 0) {
              setPendingDeactivateValues(values);
              setReassignModalOpen(true);
              return;
            }
          } catch (e) {
            message.error(
              e?.response?.data?.message || e?.message || 'Could not verify partner mappings'
            );
            return;
          }
        }

        await submitUserUpdate(values);
      } else {
        const payload = {
          name: values.name,
          email: values.email,
          mobile: values.mobile,
          entity_type: values.entity_type,
          role_id: values.role_id,
        };
        if (values.manager_id != null) payload.manager_id = values.manager_id;
        const result = await rbacService.createUser(payload);
        if (result.success) {
          message.success('User created successfully');
          setDrawerVisible(false);
          fetchUsers({ page: 1, pageSize: pagination.pageSize, currentFilters: filters, sort: sortState });
        } else {
          message.error(result.message || 'Failed to create user');
        }
      }
    } catch (error) {
      const msg =
        error.response?.data?.message ||
        error.response?.data?.error ||
        (selectedUser ? 'Error updating user' : 'Error creating user');
      message.error(msg);
      console.error('Error saving user:', error);
    }
  };

  // ── Columns ────────────────────────────────────────────────────────
  const makeSortOrder = (field) =>
    sortState.sort_by === field ? (sortState.sort_order === 'asc' ? 'ascend' : 'descend') : null;

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      width: 180,
      ellipsis: true,
      sorter: true,
      sortOrder: makeSortOrder('name'),
      render: (text) => (
        <span className="font-medium block truncate text-gray-900" title={text}>{text || '—'}</span>
      ),
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      width: 220,
      ellipsis: true,
      sorter: true,
      sortOrder: makeSortOrder('email'),
      render: (text) => (
        <span className="block truncate text-gray-600 text-xs" title={text}>{text || '—'}</span>
      ),
    },
    {
      title: 'Mobile',
      dataIndex: 'mobile',
      key: 'mobile',
      width: 120,
      ellipsis: true,
      render: (text) => (
        <span className="block truncate text-gray-600 text-xs" title={text}>{text || '—'}</span>
      ),
    },
    {
      title: 'Entity Type',
      dataIndex: 'entity_type',
      key: 'entity_type',
      width: 130,
      sorter: true,
      sortOrder: makeSortOrder('entity_type'),
      render: (text) => (
        <Tag color="blue" className="text-xs m-0! max-w-full truncate">{text || 'N/A'}</Tag>
      ),
    },
    {
      title: 'Roles',
      dataIndex: 'roles',
      key: 'roles',
      width: 200,
      render: (rolesList) => {
        const list = Array.isArray(rolesList) ? rolesList : [];
        if (!list.length) return <span className="text-gray-400 text-xs">—</span>;
        return (
          <div className="flex flex-wrap gap-1 py-0.5">
            {list.map((role, i) => (
              <Tag key={i} className="m-0! text-xs">{role}</Tag>
            ))}
          </div>
        );
      },
    },
    {
      title: 'Manager',
      dataIndex: 'manager_name',
      key: 'manager_name',
      width: 150,
      ellipsis: true,
      render: (text) => (
        <span className="block truncate text-gray-600 text-xs" title={text}>{text || '—'}</span>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'is_active',
      key: 'is_active',
      width: 85,
      render: (isActive) => (
        <Tag
          color={isActive !== false ? 'green' : 'red'}
          className="text-xs m-0!"
        >
          {isActive !== false ? 'Active' : 'Inactive'}
        </Tag>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 110,
      fixed: isAboveSm ? 'right' : undefined,
      render: (_, record) => (
        <Space size={2}>
          <Tooltip title="View Permissions">
            <Button
              type="text"
              icon={<EyeOutlined />}
              size="small"
              onClick={() => {
                setSelectedUser(record);
                setPermissionsModalVisible(true);
              }}
              className="text-gray-400! hover:text-blue-500!"
            />
          </Tooltip>
          <Tooltip title="Assign Roles">
            <Button
              type="text"
              icon={<KeyOutlined />}
              size="small"
              onClick={() => {
                setSelectedUser(record);
                setAssignRolesVisible(true);
              }}
              className="text-gray-400! hover:text-green-500!"
            />
          </Tooltip>
          <Tooltip title="Edit">
            <Button
              type="text"
              icon={<EditOutlined />}
              size="small"
              onClick={() => handleEdit(record)}
              className="text-gray-400! hover:text-yellow-500!"
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  // ── Status filter pills ────────────────────────────────────────────
  const statusOptions = [
    { label: 'All', value: undefined },
    { label: 'Active', value: true },
    { label: 'Inactive', value: false },
  ];

  return (
    <div>
      {/* ── Toolbar ── */}
      <div className="mb-3 space-y-2">

        {/* Row 1: Search + Create */}
        <div className="flex items-center gap-2 w-full justify-between">
          <Input.Search
            placeholder="Search by name, email or mobile…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onSearch={handleSearch}
            allowClear
            onClear={() => handleSearch('')}
            className="flex-1 min-w-0 sm:max-w-xs md:max-w-sm"
            size="middle"
          />
          <div className='flex gap-2'>
          
        

          <Button
            icon={<ReloadOutlined />}
            size="middle"
            onClick={() =>
              fetchUsers({
                page: pagination.current,
                pageSize: pagination.pageSize,
                currentFilters: filters,
                sort: sortState,
              })
            }
            className="text-gray-500!"
            title="Refresh"
          />
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleCreate}
            size="middle"
          >
            {isAboveSm ? 'Create User' : ''}
          </Button>
            </div>
        </div>

        {/* Row 2: Filters */}
        <div className="flex flex-wrap items-center gap-2 bg-gray-50 border border-gray-200 rounded-md px-3 py-2">

          {/* Status pills */}
          <div className="flex rounded overflow-hidden border border-gray-200 shrink-0 text-xs">
            {statusOptions.map((opt) => {
              const active =
                opt.value === undefined
                  ? filters.is_active === undefined
                  : filters.is_active === opt.value;
              return (
                <button
                  key={String(opt.value)}
                  onClick={() => handleFilterChange('is_active', opt.value)}
                  className={`px-3 py-1 transition-colors font-medium ${
                    active
                      ? 'bg-primary text-white'
                      : 'bg-white text-gray-500 hover:bg-gray-100'
                  }`}
                  style={active ? { backgroundColor: '#21B546', color: '#fff' } : {}}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>

          <div className="w-px h-5 bg-gray-200 shrink-0 hidden sm:block" />

          {/* Role */}
          <Select
            {...SEARCHABLE_SELECT_PROPS}
            placeholder="All Roles"
            value={filters.role_id}
            onChange={(v) => handleFilterChange('role_id', v)}
            allowClear
            size="small"
            className="min-w-[120px]"
            popupMatchSelectWidth={false}
          >
            {roles.map((r) => (
              <Option key={r.id} value={r.id} label={r.name}>{r.name}</Option>
            ))}
          </Select>

          {/* Entity Type */}
          <Select
            {...SEARCHABLE_SELECT_PROPS}
            placeholder="All Entity Types"
            value={filters.entity_type_id}
            onChange={(v) => handleFilterChange('entity_type_id', v)}
            allowClear
            size="small"
            className="min-w-[130px]"
            popupMatchSelectWidth={false}
          >
            {entityTypes.map((et) => (
              <Option key={et.id} value={et.id} label={et.label}>{et.label}</Option>
            ))}
          </Select>

          {/* Manager */}
          <Select
            {...SEARCHABLE_SELECT_PROPS}
            placeholder="All Managers"
            value={filters.manager_id}
            onChange={(v) => handleFilterChange('manager_id', v)}
            allowClear
            size="small"
            className="min-w-[140px]"
            popupMatchSelectWidth={false}
            options={allManagers.map((m) => ({
              value: m.id,
              label: m.entity_type ? `${m.name} (${m.entity_type})` : m.name,
            }))}
          />

          {/* Clear */}
          {activeFilterCount > 0 && (
            <>
              <div className="w-px h-5 bg-gray-200 shrink-0" />
              <button
                onClick={handleClearFilters}
                className="flex items-center gap-1 text-xs text-red-500 hover:text-red-600 font-medium transition-colors"
              >
                <CloseCircleOutlined className="text-[11px]" />
                Clear filters
                <span className="bg-red-100 text-red-600 rounded-full px-1.5 py-0.5 text-[10px] font-semibold leading-none">
                  {activeFilterCount}
                </span>
              </button>
            </>
          )}

          {/* Total count */}
          {pagination.total > 0 && (
            <span className="ml-auto text-xs text-gray-400 hidden sm:block">
              <UserOutlined className="mr-1" />
              {pagination.total} user{pagination.total !== 1 ? 's' : ''}
            </span>
          )}
        </div>
      </div>

      {/* ── Table ── */}
      <Table
        columns={columns}
        dataSource={users}
        loading={loading}
        rowKey={(record) => record.id ?? record.uuid ?? record.email ?? Math.random()}
        scroll={{ x: 1350 }}
        size="small"
        onChange={handleTableChange}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total, range) => `${range[0]}–${range[1]} of ${total}`,
          pageSizeOptions: ['10', '20', '50', '100'],
          size: 'small',
        }}
        className="rbac-users-table"
      />

      <style>{`
        .rbac-users-table .ant-table-thead > tr > th {
          background: #F9FAFB;
          font-weight: 600;
          font-size: 12px;
          color: #374151;
          border-bottom: 1px solid #E5E7EB;
          padding: 8px 10px;
        }
        .rbac-users-table .ant-table-tbody > tr > td {
          padding: 7px 10px;
          vertical-align: middle;
        }
        .rbac-users-table .ant-table-tbody > tr:hover > td {
          background: #F9FAFB;
        }
        .rbac-users-table .ant-table-column-sorter-up.active,
        .rbac-users-table .ant-table-column-sorter-down.active {
          color: #21B546;
        }
        .rbac-users-table .ant-pagination {
          margin: 12px 0 0;
        }
      `}</style>

      {/* ── Create / Edit Drawer ── */}
      <Drawer
        title={selectedUser ? 'Edit User' : 'Create New User'}
        open={drawerVisible}
        onClose={() => {
          setDrawerVisible(false);
          setSelectedUser(null);
          setManagers([]);
          form.resetFields();
        }}
        width={500}
        destroyOnClose
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          onValuesChange={handleFormValuesChange}
        >
          <Form.Item
            label="Name"
            name="name"
            rules={[{ required: true, message: 'Please enter user name' }]}
          >
            <Input placeholder="e.g., John Doe" />
          </Form.Item>

          <Form.Item
            label="Email"
            name="email"
            rules={[
              { required: true, message: 'Please enter email' },
              { type: 'email', message: 'Please enter a valid email' },
            ]}
          >
            <Input placeholder="user@example.com" />
          </Form.Item>

          <Form.Item
            label="Mobile"
            name="mobile"
            rules={[
              { required: true, message: 'Please enter mobile number' },
              { pattern: /^\d{10}$/, message: 'Enter a valid 10-digit mobile number' },
            ]}
            getValueFromEvent={(e) => e.target.value.replace(/\D/g, '').slice(0, 10)}
          >
            <Input placeholder="9876543210" maxLength={10} inputMode="numeric" />
          </Form.Item>

          {!selectedUser && (
            <Form.Item
              label="Entity Type"
              name="entity_type"
              rules={[{ required: true, message: 'Please select entity type' }]}
            >
              <Select {...SEARCHABLE_SELECT_PROPS} placeholder="Select entity type">
                {entityTypes.map((type) => (
                  <Option key={type.value} value={type.value} label={type.label}>{type.label}</Option>
                ))}
              </Select>
            </Form.Item>
          )}

          <Form.Item
            label={selectedUser ? 'Role' : 'Initial Role'}
            name="role_id"
            rules={[{ required: true, message: 'Please select a role' }]}
          >
            <Select {...SEARCHABLE_SELECT_PROPS} placeholder="Select role">
              {filterRolesByEntityType(roles, watchEntityType || selectedUser?.entity_type).map((role) => (
                <Option key={role.id} value={role.id} label={role.name}>{role.name}</Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item label="Reporting Manager" name="manager_id">
            <Select
              {...SEARCHABLE_SELECT_PROPS}
              placeholder={
                loadingManagers
                  ? 'Loading managers…'
                  : watchRoleId != null
                    ? 'Select reporting manager'
                    : 'Select role first'
              }
              allowClear
              loading={loadingManagers}
              disabled={watchRoleId == null}
            >
              {managers.map((m) => {
                const label = `${m.name}${m.role ? ` (${m.role})` : ''}`;
                return (
                  <Option key={m.id} value={m.id} label={label}>
                    {label}
                  </Option>
                );
              })}
            </Select>
          </Form.Item>

          {selectedUser && (
            <Form.Item label="Status" name="is_active" valuePropName="checked">
              <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
            </Form.Item>
          )}

          <Form.Item className="mb-0!">
            <Space>
              <Button type="primary" htmlType="submit">
                {selectedUser ? 'Update User' : 'Create User'}
              </Button>
              <Button
                onClick={() => {
                  setDrawerVisible(false);
                  setSelectedUser(null);
                  form.resetFields();
                }}
              >
                Cancel
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Drawer>

      {/* ── Assign Roles Drawer ── */}
      {selectedUser && (
        <AssignRolesDrawer
          visible={assignRolesVisible}
          user={selectedUser}
          onClose={() => {
            setAssignRolesVisible(false);
            setSelectedUser(null);
          }}
          onSuccess={() =>
            fetchUsers({
              page: pagination.current,
              pageSize: pagination.pageSize,
              currentFilters: filters,
              sort: sortState,
            })
          }
        />
      )}

      {/* ── View Permissions Modal ── */}
      {selectedUser && (
        <ViewPermissionsModal
          visible={permissionsModalVisible}
          user={selectedUser}
          onClose={() => {
            setPermissionsModalVisible(false);
            setSelectedUser(null);
          }}
        />
      )}

      <ReassignRmPartnersModal
        open={reassignModalOpen}
        rmUser={selectedUser}
        onCancel={() => {
          setReassignModalOpen(false);
          setPendingDeactivateValues(null);
        }}
        onReassigned={async () => {
          setReassignModalOpen(false);
          const values = pendingDeactivateValues;
          setPendingDeactivateValues(null);
          if (values) {
            await submitUserUpdate(values);
          }
        }}
      />
    </div>
  );
}
